import { Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import 'react-phone-number-input/style.css';
import ProfileFirstName from './ProfileComponents/ProfileFirstName';
import ProfileLastName from './ProfileComponents/ProfileLastName';
import ProfilePhone from './ProfileComponents/ProfilePhone';
import ProfileTitle from './ProfileComponents/ProfileTitle';
import ProfileImage from './ProfileImage';

export default function ProfileManagementDialog({
  open,
  isLoading,
  data,
  amplifyApi,
  onClose,
  onSave,
  mailto = 'support@diamonddigitalpro.com',
}) {
  const { t } = useTranslation('global');
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));

  const contentDirection = mobile ? 'column-reverse' : 'row';
  const contentPadding = mobile ? 3 : 5;

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { isValid, errors, isDirty },
  } = useForm({
    mode: 'all',
  });

  const onCloseHandler = () => {
    if (isLoading || !onClose) return;
    reset({
      firstName: data.firstName,
      lastName: data.lastName,
      title: data.title,
      phone: data.phone,
      photo: data.photo,
    });
    onClose();
  };

  useEffect(() => {
    if (!data) return;
    reset({
      firstName: data.firstName,
      lastName: data.lastName,
      title: data.title,
      phone: data.phone,
      photo: data.photo,
    });
  }, [reset, data]);

  return (
    <Dialog open={open} fullWidth={true} onClose={onCloseHandler}>
      <DialogTitle
        component={Stack}
        justifyContent="space-between"
        alignItems="center"
        direction="row"
        sx={{ p: 2, background: theme.palette.grey[50] }}
      >
        <Typography variant="subtitle2">{t('modalProfile.title')}</Typography>
        <IconButton
          aria-label="close"
          size="small"
          onClick={onCloseHandler}
          sx={{ color: theme.palette.grey[500] }}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack direction={contentDirection} gap={6} py={contentPadding}>
          <Stack gap={4} flexGrow={1}>
            <ProfileFirstName errors={errors} data={data} register={register} />
            <ProfileLastName errors={errors} data={data} register={register} />
            <ProfileTitle errors={errors} data={data} register={register} />
            <ProfilePhone control={control} data={data} errors={errors} />
            <Stack>
              <TextField
                label={t('modalProfile.email')}
                type="email"
                size="small"
                defaultValue={data?.email}
                disabled={true}
                sx={{
                  '& fieldset': {
                    borderStyle: 'dashed',
                  },
                }}
              />
              <Typography variant="caption" color="text.disabled">
                {t('modalProfile.emailHelp')}
                <Link variant="caption" href={`mailto:${mailto}`}>
                  {t('modalProfile.here')}
                </Link>
              </Typography>
            </Stack>
          </Stack>
          <Stack alignItems="center">
            <ProfileImage
              amplifyApi={amplifyApi}
              url={data?.photo}
              onChange={photo => {
                setValue('photo', photo?.fileId || null, { shouldDirty: true });
              }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="outlined"
          disabled={isLoading}
          onClick={onCloseHandler}
          sx={{
            color: 'black',
            borderColor: 'lightgray',
            '&:hover': {
              backgroundColor: 'lightgray',
              borderColor: 'lightgray',
            },
          }}
        >
          {t('modalProfile.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isLoading}
          disabled={!isValid || !isDirty}
          onClick={handleSubmit(onSave)}
        >
          {t('modalProfile.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
